import { RoleType, useCurrentUser } from '@feature/security';
import { HierarchyDisciplineSelect, HierarchyFacultySelect } from '@feature/utils';
import { HierarchyDepartmentSelect } from '@feature/utils/components/HierarchyDepartmentSelect';
import {
  useDepartmentAdminHierarchy,
  useDisciplineAdminHierarchy,
  useFacultyAdminHierarchy,
  useHierarchySelection,
  useUniversityAdminHierarchy,
} from '@feature/utils/components/hooks/hierarchy/useHierarchy';

import { UserDocumentScope } from '../model/UserDocumentTypes';

const EMPTY_SCOPE: UserDocumentScope = {
  facultyId: null,
  departmentId: null,
  disciplineId: null,
};

/**
 * The hierarchy selection lives in the url and can hold ids left over from another screen or a wider
 * role. Only the ids the current role may send are kept; the server 403s the rest. `isReady` is false
 * while the role's mandatory id is missing, which the server would answer with a 409.
 */
export function useUserDocumentScope(): UserDocumentScope & { isReady: boolean } {
  const { currentRole } = useCurrentUser();
  const { facultyId, departmentId, disciplineId } = useHierarchySelection();

  if (currentRole === RoleType.UNIVERSITY_ADMIN) {
    return { facultyId, departmentId, disciplineId, isReady: true };
  }

  if (currentRole === RoleType.FACULTY_ADMIN) {
    return { facultyId, departmentId, disciplineId, isReady: facultyId != null };
  }

  if (currentRole === RoleType.DEPARTMENT_ADMIN) {
    return { ...EMPTY_SCOPE, departmentId, disciplineId, isReady: departmentId != null };
  }

  if (currentRole === RoleType.DISCIPLINE_ADMIN) {
    return { ...EMPTY_SCOPE, disciplineId, isReady: disciplineId != null };
  }

  return { ...EMPTY_SCOPE, isReady: false };
}

function UniversityOrgFilter() {
  const { selectProps } = useUniversityAdminHierarchy();
  return (
    <HierarchyFacultySelect
      {...selectProps}
      showUsers={false}
    />
  );
}

function FacultyOrgFilter() {
  const { selectProps } = useFacultyAdminHierarchy();
  return (
    <HierarchyFacultySelect
      {...selectProps}
      showUsers={false}
    />
  );
}

function DepartmentOrgFilter() {
  const { selectProps } = useDepartmentAdminHierarchy();
  return (
    <HierarchyDepartmentSelect
      {...selectProps}
      showUsers={false}
    />
  );
}

function DisciplineOrgFilter() {
  const { selectProps } = useDisciplineAdminHierarchy();
  return (
    <HierarchyDisciplineSelect
      {...selectProps}
      showUsers={false}
    />
  );
}

/** Renders only the unit pickers the current role heads; the selection itself lives in the url. */
export function UserDocumentOrgFilter() {
  const { currentRole } = useCurrentUser();

  if (currentRole === RoleType.UNIVERSITY_ADMIN) {
    return <UniversityOrgFilter />;
  }

  if (currentRole === RoleType.FACULTY_ADMIN) {
    return <FacultyOrgFilter />;
  }

  if (currentRole === RoleType.DEPARTMENT_ADMIN) {
    return <DepartmentOrgFilter />;
  }

  if (currentRole === RoleType.DISCIPLINE_ADMIN) {
    return <DisciplineOrgFilter />;
  }

  return null;
}
